import type { Attachment, ChatMessage } from '@/api/chat';

/**
 * Как сотруднику показать файл, который ученик прикрепил в чате (12.10.2026, техдолг 389).
 *
 * 🔴 ЧТО БЫЛО. С 1.1.1 (07.10) и сайта (06.10) ученик шлёт файлы в чат, а кабинет куратора рисовал только текст:
 * фото без подписи давало пустой пузырь, в списке слева — «Нет сообщений». Админка ссылку рисовала, но адрес брала
 * как есть.
 *
 * ⚠️ ВИД — ПО РАСШИРЕНИЮ, А НЕ ПО `type`. Приложение и сайт шлют `image` только для картинок, всё остальное — `file`:
 * и видео, и PDF (`StudentChatPage.tsx`, `onPickFile`). Размера и mime в сообщении нет вовсе — chat-service их не
 * хранит (`message_attachments`: url, type, name), поэтому размер не показываем: без правки службы его не взять.
 *
 * ⚠️ АДРЕС НЕ ПРОВЕРЯЕТ НИКТО, КРОМЕ НАС. Служба пишет `url` как пришёл. Ученик сырым кадром сокета кладёт
 * `javascript:…` — и нажатие куратора или админа выполнило бы его в их сессии. Пропускаем только http(s) и
 * относительный путь к файлам платформы.
 */

export type AttachmentKind = 'image' | 'video' | 'audio' | 'pdf' | 'file';

const IMAGE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif'];
const VIDEO = ['mov', 'mp4', 'm4v', 'webm', '3gp'];
const AUDIO = ['m4a', 'mp3', 'aac', 'wav', 'ogg'];

/** Расширение из имени или адреса, без `?…` и `#…`, строчными. */
function extensionOf(s?: string): string {
  if (!s) return '';
  const path = s.split(/[?#]/)[0];
  const m = /\.([a-z0-9]{1,5})$/i.exec(path);
  return m ? m[1].toLowerCase() : '';
}

export function attachmentKind(a: Pick<Attachment, 'type' | 'name' | 'url'>): AttachmentKind {
  const ext = extensionOf(a.name) || extensionOf(a.url);
  if (IMAGE.includes(ext)) return 'image';
  if (VIDEO.includes(ext)) return 'video';
  if (AUDIO.includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (a.type === 'image') return 'image';
  if (a.type === 'audio') return 'audio';
  return 'file';
}

/** Адрес, который можно отдать в `href` / `src`, или `null`. */
export function safeAttachmentUrl(url?: string): string | null {
  const u = (url ?? '').trim();
  if (!u || u !== url) return null;
  if (u.startsWith('/api/v1/files/o/')) return u;
  if (!/^https?:\/\//i.test(u)) return null;
  try {
    const parsed = new URL(u);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? u : null;
  } catch {
    return null;
  }
}

/**
 * Имя для подписи: присланное, иначе хвост ключа хранилища без случайной приставки (`<rnd>-<имя>`), иначе пусто —
 * тогда экран подписывает видом («Видео», «Файл»).
 */
export function attachmentName(a: Pick<Attachment, 'name' | 'url'>): string {
  const name = a.name?.trim();
  if (name) return name;
  const tail = (a.url ?? '').split(/[?#]/)[0].split('/').pop() ?? '';
  let decoded = tail;
  try {
    decoded = decodeURIComponent(tail);
  } catch {
    // битая кодировка — оставляем как есть
  }
  // Приставка — `RandomKey()` из shared/storage: 16 байт в hex.
  return decoded.replace(/^[0-9a-f]{32}-/i, '');
}

type T = (key: string, opts?: Record<string, unknown>) => string;

export function kindLabel(kind: AttachmentKind, t: T): string {
  switch (kind) {
    case 'image':
      return t('staffChatAttach.kindImage');
    case 'video':
      return t('staffChatAttach.kindVideo');
    case 'audio':
      return t('staffChatAttach.kindAudio');
    case 'pdf':
      return t('staffChatAttach.kindPdf');
    default:
      return t('staffChatAttach.kindFile');
  }
}

/**
 * Строка последнего сообщения в списке диалогов куратора. Текст есть — он; текста нет, а файл есть — «Вложение: имя»;
 * ничего — `null` (экран пишет «Нет сообщений»).
 */
export function lastMessagePreview(msg: Pick<ChatMessage, 'content' | 'attachments'> | undefined, t: T): string | null {
  if (!msg) return null;
  const text = msg.content?.trim();
  if (text) return text;
  const first = msg.attachments?.[0];
  if (!first) return null;
  const name = attachmentName(first) || kindLabel(attachmentKind(first), t);
  return t('staffChatAttach.listPreview', { name });
}
